/**
 * Error Page Component
 * Displays error information for route-level errors
 */
import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Card, CardContent, Container } from '@mui/material';
import { ErrorOutline, Refresh, Home, ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const ErrorPage = () => {
  const { t } = useTranslation();
  const error = useRouteError();
  const navigate = useNavigate();

  let errorMessage = t('errorPages.defaultMessage');
  let errorStatus = 500;
  let errorDetails: string | null = null;

  if (isRouteErrorResponse(error)) {
    errorMessage = error.statusText || errorMessage;
    errorStatus = error.status;
    if (error.data) {
      errorDetails = typeof error.data === 'string' ? error.data : JSON.stringify(error.data);
    }
  } else if (error instanceof Error) {
    errorMessage = error.message;
    errorDetails = error.stack || null;
  }

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const getErrorTitle = () => {
    switch (errorStatus) {
      case 404:
        return t('errorPages.status.404');
      case 403:
        return t('errorPages.status.403');
      case 401:
        return t('errorPages.status.401');
      case 500:
        return t('errorPages.status.500');
      default:
        return t('errorPages.status.default');
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Card
        sx={{
          boxShadow: 4,
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            background: () =>
              errorStatus === 404
                ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
                : errorStatus === 403 || errorStatus === 401
                ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                : 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: 'white',
            p: 4,
            textAlign: 'center',
          }}
        >
          <ErrorOutline sx={{ fontSize: 80, mb: 2 }} />
          <Typography variant="h3" fontWeight={700} gutterBottom>
            {errorStatus}
          </Typography>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            {getErrorTitle()}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, mt: 2 }}>
            {errorMessage}
          </Typography>
        </Box>

        <CardContent sx={{ p: 4 }}>
          {errorDetails && process.env.NODE_ENV === 'development' && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom fontWeight={600}>
                {t('errorPages.detailsTitle')}
              </Typography>
              <Box
                sx={{
                  bgcolor: 'action.hover',
                  p: 2,
                  borderRadius: 1,
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {errorDetails}
                </Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<ArrowBack />}
              onClick={handleGoBack}
              sx={{ minWidth: 150 }}
            >
              {t('errorPages.buttons.goBack')}
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<Home />}
              onClick={handleGoHome}
              sx={{ minWidth: 150 }}
            >
              {t('errorPages.buttons.goHome')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<Refresh />}
              onClick={handleReload}
              sx={{ minWidth: 150 }}
            >
              {t('errorPages.buttons.reload')}
            </Button>
          </Box>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {errorStatus === 404
                ? t('errorPages.notFoundHelp')
                : t('errorPages.supportHelp')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ErrorPage;

